import React, { useState, useEffect } from "react";
import Container from "react-bootstrap/Container";
import Form from "react-bootstrap/Form";
import Row from "react-bootstrap/Row";
import Col from "react-bootstrap/Col";
// eslint-disable-next-line @typescript-eslint/no-unused-vars
import Badge from "react-bootstrap/Badge";
import { TextEditor } from "pymble-ui";
import "pymble-ui/styles.css";

// eslint-disable-next-line @typescript-eslint/no-explicit-any
function formatDate(dateString: any) {
	const d = new Date(dateString);

	const date = d.toLocaleString("default", {
		year: "numeric",
		month: "long",
		day: "numeric",
		hour12: true,
	});

	const time = d.toLocaleTimeString([], {
		hour: "2-digit",
		minute: "2-digit",
	});
	return date + " " + time;
}

function removeHtmlEntities(data: string): string {
	const parser = new DOMParser();
	const decodedHtml = parser.parseFromString(data, "text/html");
	return decodedHtml.documentElement.textContent || "";
}

type EventDetailsData = {
	AdditionalNotes: string;
	Description: {
		Long: string;
		Short: string;
	};
	// Only sent when the event is fetched with supervisors=1 (useEventManager).
	Supervisors?: {
		Name: string;
	}[];
	Location: {
		Name: string;
		Address: string;
		Lat: string;
		Lon: string;
	};
	Approval: {
		Urls: {
			MoreInfo: string;
			Event: string;
		};
	};
	Title: string;
	MasterEventId: number;
	StartDateTime: string;
	EndDateTime: string;
};

type EventDetailsProps = {
	initialData: EventDetailsData;
	onValueChange: (data: EventDetailsData) => void;
};

export const EventDetails: React.FC<EventDetailsProps> = ({
	initialData,
	onValueChange,
}) => {
	const [data, setData] = useState<EventDetailsData>(initialData || {} as EventDetailsData);
	const supervisor = data?.Supervisors || [];
	const [formUrl, setFormUrl] = useState(initialData?.Approval?.Urls?.Event);
	const [moreInfoUrl, setMoreInfoUrl] = useState(
		initialData?.Approval?.Urls?.MoreInfo
	);

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const handleNotesChange = (event: any) => {
		setData((prevData) => ({
			...prevData,
			AdditionalNotes: event,
		}));
	};

	const handleDescriptionChange = (value: string) => {
		setData((prevData) => ({
			...prevData,
			Description: { ...prevData.Description, Long: value },
		}));
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
	const handleMoreInfoChange = (event: any) => {
		const { value } = event.target;
		setMoreInfoUrl(() => value);

		setData((prevData) => ({
			...prevData,
			Approval: {
				Urls: {
					MoreInfo: value,
					Event: formUrl,
				},
			},
		}));
	};

	// eslint-disable-next-line @typescript-eslint/no-explicit-any, @typescript-eslint/no-unused-vars
	const handleFormUrlChange = (event: any) => {
		const { value } = event.target;
		setFormUrl(() => value);

		setData((prevData) => ({
			...prevData,
			Approval: {
				Urls: {
					MoreInfo: moreInfoUrl,
					Event: value,
				},
			},
		}));
	};

	useEffect(() => {
		onValueChange(data);
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [data]);

	return (
		<>
			<Container>
				<Row className="mb-3">
					<Col className="col-md-12">
						<h3>Details</h3>
					</Col>
				</Row>
				<Row className="mb-3">
					<Col className="col-md-6">
						<Form.Group className="">
							<Form.Label>Event</Form.Label>
							<Form.Control
								placeholder="Event name"
								id="event_name"
								name="Title"
								value={removeHtmlEntities(data?.Title || "")}
								disabled
								readOnly
							/>
						</Form.Group>
					</Col>
					{data.MasterEventId && (
						<Col className="col-md-3">
							<Form.Group className="">
								<Form.Label> </Form.Label>
								<Form.Control
									plaintext
									readOnly
									defaultValue="*Reccurring event"
								/>
							</Form.Group>
						</Col>
					)}
				</Row>
				<Row className="mb-3">
					<Col className="col-md-3">
						<Form.Group className="">
							<Form.Label>Start Date</Form.Label>
							<Form.Control
								type="text"
								placeholder="Start date"
								id="start_date"
								value={data?.StartDateTime ? formatDate(data.StartDateTime) : ""}
								disabled
								readOnly
							/>
						</Form.Group>
					</Col>

					<Col className="col-md-3">
						<Form.Group className="">
							<Form.Label>End Date</Form.Label>
							<Form.Control
								type="text"
								placeholder="End date"
								id="end_date"
								value={data?.EndDateTime ? formatDate(data.EndDateTime) : ""}
								disabled
								readOnly
							/>
						</Form.Group>
					</Col>
					<Col className="col-md-3">
						<Form.Group className="">
							<Form.Label>Event Owner</Form.Label>
							<Form.Control
								type="text"
								placeholder=""
								id="owner"
								disabled
								readOnly
								value={supervisor[0]?.Name || ""}
							/>
						</Form.Group>
					</Col>
					<Col className="col-md-3">
						<Form.Group className="">
							<Form.Label>Location</Form.Label>
							<Form.Control
								type="text"
								id="location"
								disabled
								readOnly
								value={removeHtmlEntities(data?.Location?.Name || "") || ""}
							/>
						</Form.Group>
					</Col>
				</Row>
				{/* <Row className="mb-3">
					<Col className="col-md-6">
						<Form.Label htmlFor="additional_info">
							Additional Information URL
						</Form.Label>
						<Form.Control
							type="text"
							id="more_info"
							name="MoreInfo"
							onChange={handleMoreInfoChange}
							value={moreInfoUrl || ""}
						/>
					</Col>
					<Col className="col-md-6">
						<Form.Label htmlFor="form_url">
							Additional Data Collection Form URL
						</Form.Label>
						<Form.Control
							type="text"
							className="form-control"
							id="form_url"
							name="Event"
							value={formUrl || ""}
							onChange={handleFormUrlChange}
						/>
					</Col>
				</Row> */}
				<Row className="mb-3">
					<Col className="col-md-12">
						<Form.Label htmlFor="description">Description</Form.Label>
						<TextEditor
							content={data?.Description?.Long || ""}
							onUpdate={handleDescriptionChange}
						/>
					</Col>
				</Row>
				<Row className="mb-3">
					<Col className="col-md-12">
						<Form.Label htmlFor="additional_notes">Additional Notes</Form.Label>
						<TextEditor
							content={data?.AdditionalNotes || ""}
							onUpdate={handleNotesChange}
						/>
					</Col>
				</Row>
			</Container>
		</>
	);
};
